import React from 'react';
import { MdClose, MdLocationOn, MdWarning } from 'react-icons/md';
import { US_STATES } from '../../constants/locations';

function CreateRepProfileModal({
  formData,
  offices,
  submitting,
  error,
  onInputChange,
  onSubmit,
  onClose,
}) {
  const isCityCountyRequired = formData.office_level === 'Local';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Create Representative Profile</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <MdClose className="text-2xl" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={onInputChange}
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                data-testid="rep-first-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={onInputChange}
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                data-testid="rep-last-name"
              />
            </div>
          </div>

          {/* Email Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Primary Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="primary_email"
              value={formData.primary_email}
              onChange={onInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              data-testid="rep-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Secondary Email (Optional)
            </label>
            <input
              type="email"
              name="secondary_email"
              value={formData.secondary_email}
              onChange={onInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <label className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                name="secondary_email_public"
                checked={formData.secondary_email_public}
                onChange={onInputChange}
                className="w-4 h-4 text-primary rounded"
              />
              <span className="text-sm text-gray-600">Make secondary email public</span>
            </label>
          </div>

          {/* Office Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Office Level <span className="text-red-500">*</span>
              </label>
              <select
                name="office_level"
                value={formData.office_level}
                onChange={onInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                data-testid="rep-office-level"
              >
                <option value="">Select level...</option>
                <option value="National">National</option>
                <option value="State">State</option>
                <option value="Local">Local</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Office <span className="text-red-500">*</span>
              </label>
              <select
                name="office_id"
                value={formData.office_id}
                onChange={onInputChange}
                disabled={!formData.office_level}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-gray-100"
                data-testid="rep-office-select"
              >
                <option value="">Select office...</option>
                {formData.office_level && offices[formData.office_level]?.map((office) => (
                  <option key={office.office_id} value={office.office_id}>
                    {office.position_title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Running Location */}
          {formData.office_level && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-3">
                <MdLocationOn className="text-xl text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">Running Location</h4>
                  <p className="text-sm text-blue-700">
                    {formData.office_level === 'Local'
                      ? 'Please specify the state and city/county where you are running for this local position.'
                      : 'Please specify the state where you are running for this position.'}
                  </p>
                </div>
              </div>

              <div className={`grid ${isCityCountyRequired ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="running_state"
                    value={formData.running_state}
                    onChange={onInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white"
                    data-testid="rep-running-state"
                  >
                    <option value="">Select state...</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>

                {isCityCountyRequired && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City/County <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="running_city_county"
                      value={formData.running_city_county}
                      onChange={onInputChange}
                      placeholder="e.g., Los Angeles or Orange County"
                      maxLength={100}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                      data-testid="rep-running-city"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Biography */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Biography (Optional)
            </label>
            <textarea
              name="biography"
              value={formData.biography}
              onChange={onInputChange}
              maxLength={2000}
              rows={4}
              placeholder="Tell voters about yourself, your experience, and your platform..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
              data-testid="rep-biography"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.biography.length}/2000 characters</p>
          </div>

          {/* Disclaimers */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
            <div className="flex items-start gap-2">
              <MdWarning className="text-xl text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800">Important Disclaimers</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Please read and acknowledge the following before creating your profile.
                </p>
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="disclaimer_acknowledged"
                checked={formData.disclaimer_acknowledged}
                onChange={onInputChange}
                className="w-5 h-5 text-primary rounded mt-0.5"
                data-testid="rep-disclaimer-1"
              />
              <span className="text-sm text-gray-700">
                I understand that creating this profile does not constitute official registration to run for political office.
                I must follow all local rules and regulations of my jurisdiction to appear on an election ballot.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="poll_responses_public_acknowledged"
                checked={formData.poll_responses_public_acknowledged}
                onChange={onInputChange}
                className="w-5 h-5 text-primary rounded mt-0.5"
                data-testid="rep-disclaimer-2"
              />
              <span className="text-sm text-gray-700">
                I understand that my poll responses will be made public and visible to all users.
                Voters will be able to see how I voted and any explanations I provide.
              </span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              data-testid="rep-submit-btn"
            >
              {submitting ? 'Creating...' : 'Create Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateRepProfileModal;
